// src/utils/preloadErrorReload.ts
//
// After a deploy, a tab still running the previous bundle asks for lazy-route chunks whose hashed
// filenames no longer exist; Vite then dispatches `vite:preloadError` on window and, unless the
// event is cancelled, throws — a white screen. Reloading picks up the new index.html and chunks.
// The reload is guarded by a timestamp in sessionStorage so a chunk that is genuinely missing (or a
// server that is down) can't put the tab into a reload loop: a second failure within the window is
// left to throw, and the app's error boundary shows its "Something went wrong — Reload" screen.

export const PRELOAD_RELOAD_KEY = 'da-connect:preload-error-reload-at';

/** A second preload failure within this long of a guarded reload is not reloaded again. */
export const PRELOAD_RELOAD_WINDOW_MS = 60_000;

type StorageLike = Pick<Storage, 'getItem' | 'setItem'>;

interface Deps {
    reload?: () => void;
    now?: () => number;
    storage?: StorageLike;
}

// Set once a guarded reload has been asked for. Cancelling the event makes the failed lazy import
// resolve to nothing, so React throws to the error boundary in the moment before the page goes —
// the boundary checks this and shows a neutral loader instead of flashing its error screen.
let reloadPending = false;

/** Whether a stale-chunk reload is already on its way. */
export const isReloadPending = (): boolean => reloadPending;

/** Test-only: forget a pending reload (module state outlives a single test). */
export function resetPreloadReloadState(): void {
    reloadPending = false;
}

export function handlePreloadError(event: Event, deps: Deps = {}): void {
    const reload = deps.reload ?? (() => window.location.reload());
    const now = (deps.now ?? Date.now)();

    try {
        // Inside the try: merely reading window.sessionStorage throws when storage is blocked.
        const storage = deps.storage ?? window.sessionStorage;
        const last = Number(storage.getItem(PRELOAD_RELOAD_KEY));
        if (Number.isFinite(last) && last > 0 && now - last < PRELOAD_RELOAD_WINDOW_MS) return;
        storage.setItem(PRELOAD_RELOAD_KEY, String(now));
    } catch {
        // No storage, no loop guard — don't risk an endless reload; the boundary handles it.
        return;
    }

    reloadPending = true;
    event.preventDefault();
    reload();
}

/** Installs the listener once, from main.tsx. */
export function installPreloadErrorReload(): void {
    window.addEventListener('vite:preloadError', (event) => handlePreloadError(event));
}
